import { cva, type VariantProps } from "class-variance-authority";
import {
	createContext,
	type FC,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useContext,
} from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const cardVariants = cva(
	"relative w-full h-fit min-h-fit rounded-md border border-bc-default bg-surface-base text-secondary z-10",
	{
		variants: {
			// Depth — absorbs the shadow-sm cards and the StackedCard double-div effect.
			elevation: {
				flat: "",
				raised: "shadow-sm",
			},
			density: {
				default: "",
				compact: "rounded-lg",
			},
			edge: {
				none: "",
				info: "before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:rounded-l-[inherit] before:bg-intent-info-text",
			},
		},
		defaultVariants: { elevation: "flat", density: "default", edge: "none" },
	},
);

type Density = NonNullable<VariantProps<typeof cardVariants>["density"]>;

const DensityContext = createContext<Density>("default");

type RootProps = HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof cardVariants> & {
		trackingId?: string;
		children?: ReactNode;
		ref?: Ref<HTMLDivElement>;
	};

const Root: FC<RootProps> = ({
	elevation,
	density,
	edge,
	trackingId,
	className,
	children,
	ref,
	...rest
}) => (
	<DensityContext.Provider value={density ?? "default"}>
		<div
			ref={ref}
			data-density={density ?? "default"}
			className={cn(cardVariants({ elevation, density, edge }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{children}
		</div>
	</DensityContext.Provider>
);
Root.displayName = "Card";

type HeaderProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Header: FC<HeaderProps> = ({ trackingId, className, ref, ...rest }) => {
	const compact = useContext(DensityContext) === "compact";
	return (
		<div
			ref={ref}
			className={cn(
				"w-full flex items-center justify-between border-b border-b-bc-default rounded-t-[inherit] bg-surface-base",
				compact ? "px-3 py-2" : "px-6 py-4",
				className,
			)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
};
Header.displayName = "Card.Header";

const Title: FC<HeaderProps> = ({ trackingId, className, ref, ...rest }) => {
	const compact = useContext(DensityContext) === "compact";
	return (
		<div
			ref={ref}
			className={cn(
				"text-primary",
				compact ? "text-[14px] font-semibold" : "text-[17px] font-medium",
				className,
			)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
};
Title.displayName = "Card.Title";

const Actions: FC<HeaderProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("flex items-center gap-2", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Actions.displayName = "Card.Actions";

// `gray` body reproduces the CardGrayBody gray panel; `plain` is transparent.
const bodyVariants = cva("w-full h-fit min-h-fit rounded-b-[inherit]", {
	variants: {
		surface: { plain: "", gray: "bg-surface-light" },
		density: { default: "p-4", compact: "p-3" },
	},
	defaultVariants: { surface: "plain", density: "default" },
});

type BodyProps = HTMLAttributes<HTMLDivElement> &
	Pick<VariantProps<typeof bodyVariants>, "surface"> & {
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

const Body: FC<BodyProps> = ({ surface, trackingId, className, ref, ...rest }) => {
	const density = useContext(DensityContext);
	return (
		<div
			ref={ref}
			className={cn(bodyVariants({ surface, density }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
};
Body.displayName = "Card.Body";

export const Card = Object.assign(Root, { Header, Title, Actions, Body });

export type CardProps = RootProps;
export type CardHeaderProps = HeaderProps;
export type CardBodyProps = BodyProps;
